import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { temNivelMinimo } from "../../../utils/permissoes";
import { NIVEIS } from "../../../config/niveis";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { db } from "../../../config/firebase";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { uploadToStorage } from "../../../utils/storageUpload";

const PERIODICIDADE_ITEM_PADRAO = "MENSAL";
const PERIODICIDADE_ITEM_OPCOES = [
  { value: "QUINZENAL", label: "Quinzenal" },
  { value: "MENSAL", label: "Mensal" },
  { value: "TRIMESTRAL", label: "Trimestral" },
  { value: "SEMESTRAL", label: "Semestral" },
];


function EditarPreventiva() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isMobile = useIsMobile(900);

  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const [nome, setNome] = useState("");
  const [fabricante, setFabricante] = useState("");
  const [modelo, setModelo] = useState("");

  const [codigo, setCodigo] = useState("");
  const [empresaId, setEmpresaId] = useState("sem_empresa");
  const [grupoIdPreventiva, setGrupoIdPreventiva] = useState("");

  const [itens, setItens] = useState([]);
  const inputItemFotoCameraRefs = useRef([]);
  const inputItemFotoArquivoRefs = useRef([]);

  useEffect(() => {
    let ativo = true;

    async function carregarDados() {
      if (!usuarioLogado?.grupoId || !id) {
        setCarregando(false);
        return;
      }

      if (!temNivelMinimo(usuarioLogado, NIVEIS.ADMIN)) {
        setErro("Apenas Administrador/Gestor pode editar preventivas.");
        setCarregando(false);
        return;
      }

      setCarregando(true);
      setErro("");
      setSucesso("");

      try {
        const ref = doc(db, "preventivasModelos", id);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          if (ativo) {
            setErro("Preventiva não encontrada.");
          }
          return;
        }

        const dados = snap.data();

        if (
          dados.grupoId &&
          usuarioLogado.grupoId &&
          dados.grupoId !== usuarioLogado.grupoId
        ) {
          if (ativo) {
            setErro("Você não tem permissão para editar esta preventiva.");
          }
          return;
        }

        if (!ativo) return;

        setGrupoIdPreventiva(dados.grupoId || usuarioLogado.grupoId);
        setEmpresaId(dados.empresaId || "sem_empresa");
        setCodigo(dados.codigo || "");
        setNome(dados.nome || "");
        setFabricante(dados.fabricante || "");
        setModelo(dados.modelo || "");
        setItens(
          Array.isArray(dados.itens)
            ? dados.itens.map((item) => ({
                pergunta: item?.pergunta || "",
                tipoResposta: item?.tipoResposta || "SIM_NAO",
                periodicidade: item?.periodicidade || PERIODICIDADE_ITEM_PADRAO,
                fotoUrl: item?.fotoUrl || null,
                fotoFile: null,
                fotoPreview: null,
              }))
            : []
        );
      } catch (err) {
        console.error(err);
        if (ativo) {
          setErro(
            "Erro ao carregar preventiva" +
              (err?.message ? `: ${err.message}` : "")
          );
        }
      } finally {
        if (ativo) {
          setCarregando(false);
        }
      }
    }

    carregarDados();

    return () => {
      ativo = false;
      inputItemFotoCameraRefs.current = [];
      inputItemFotoArquivoRefs.current = [];
    };
  }, [id, usuarioLogado]);

  const adicionarItem = () => {
    setItens((prev) => [
      ...prev,
      {
        pergunta: "",
        tipoResposta: "SIM_NAO",
        periodicidade: PERIODICIDADE_ITEM_PADRAO,
        fotoUrl: null,
        fotoFile: null,
        fotoPreview: null,
      },
    ]);
  };

  const atualizarItem = (index, campo, valor) => {
    setItens((prev) => {
      const novo = [...prev];
      novo[index] = {
        ...novo[index],
        [campo]: valor,
      };
      return novo;
    });
  };

  const removerItem = (index) => {
    setItens((prev) => prev.filter((_, i) => i !== index));
  };

  const atualizarFotoItem = (index, file) => {
    setItens((prev) => {
      const novo = [...prev];
      const atual = { ...(novo[index] || {}) };

      if (atual.fotoPreview) {
        try {
          URL.revokeObjectURL(atual.fotoPreview);
        } catch (e) {}
      }

      if (file) {
        atual.fotoFile = file;
        atual.fotoPreview = URL.createObjectURL(file);
      } else {
        atual.fotoFile = null;
        atual.fotoPreview = null;
        atual.fotoUrl = null;
      }

      novo[index] = atual;
      return novo;
    });
  };

  const removerFotoItem = (index) => {
    setItens((prev) => {
      const novo = [...prev];
      const atual = { ...(novo[index] || {}) };

      if (atual.fotoPreview) {
        try {
          URL.revokeObjectURL(atual.fotoPreview);
        } catch (e) {}
      }

      atual.fotoFile = null;
      atual.fotoPreview = null;
      atual.fotoUrl = null;
      novo[index] = atual;
      return novo;
    });
  };

  const handleSubmit = async (evento) => {
    evento.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuarioLogado?.grupoId) {
      setErro(
        "Usuário logado não possui grupo definido. Verifique o cadastro do Gestor."
      );
      return;
    }

    if (!temNivelMinimo(usuarioLogado, NIVEIS.ADMIN)) {
      setErro("Apenas Administrador/Gestor pode editar preventivas.");
      return;
    }

    if (!nome.trim()) {
      setErro("Informe o nome da preventiva.");
      return;
    }

    if (!fabricante.trim()) {
      setErro("Informe o fabricante da máquina/modelo.");
      return;
    }

    if (!modelo.trim()) {
      setErro("Informe o modelo da máquina.");
      return;
    }


    try {
      setSalvando(true);
      const grupoId = grupoIdPreventiva || usuarioLogado.grupoId;

      const itensProcessados = [];
      for (let i = 0; i < itens.length; i += 1) {
        const item = itens[i] || {};
        let fotoUrl = item.fotoUrl || null;

        if (item.fotoFile) {
          fotoUrl = await uploadToStorage({
            grupoId,
            empresaId: empresaId || "sem_empresa",
            pasta: "preventivas/itens",
            file: item.fotoFile,
            filenamePrefix: `${codigo || id || "preventiva"}_item${String(
              i + 1
            ).padStart(2, "0")}`,
          });
        }

        itensProcessados.push({
          pergunta: (item.pergunta || "").trim(),
          tipoResposta: item.tipoResposta || "SIM_NAO",
          periodicidade: item.periodicidade || PERIODICIDADE_ITEM_PADRAO,
          fotoUrl: fotoUrl || null,
        });
      }

      await updateDoc(doc(db, "preventivasModelos", id), {
        nome: nome.trim(),
        fabricante: fabricante.trim(),
        modelo: modelo.trim(),
        itens: itensProcessados,
        updatedAt: serverTimestamp(),
      });

      setSucesso("Preventiva atualizada com sucesso!");
    } catch (err) {
      console.error(err);
      setErro(
        "Erro ao salvar preventiva" + (err?.message ? `: ${err.message}` : "")
      );
    } finally {
      setSalvando(false);
    }
  };

  const renderFormulario = () => {
    return (
      <>
        {erro && (
          <div
            style={{
              marginTop: 12,
              padding: 8,
              borderRadius: 8,
              background: "#7f1d1d",
              color: "#fecaca",
              fontSize: 14,
            }}
          >
            {erro}
          </div>
        )}

        {sucesso && (
          <div
            style={{
              marginTop: 12,
              padding: 8,
              borderRadius: 8,
              background: "#14532d",
              color: "#bbf7d0",
              fontSize: 14,
            }}
          >
            {sucesso}
          </div>
        )}

        {carregando ? (
          <p style={{ marginTop: 16, fontSize: 14, opacity: 0.8 }}>
            Carregando preventiva...
          </p>
        ) : (
          <form
            onSubmit={handleSubmit}
            style={{
              marginTop: 12,
              maxWidth: 720,
              marginLeft: "auto",
              marginRight: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <label>
              <span style={{ fontSize: 14 }}>Nome da preventiva</span>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                }}
              />
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Fabricante</span>
              <input
                type="text"
                value={fabricante}
                onChange={(e) => setFabricante(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                }}
              />
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Modelo</span>
              <input
                type="text"
                value={modelo}
                onChange={(e) => setModelo(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                }}
              />
            </label>

            <div style={{ opacity: 0.85, fontSize: 13 }}>
              Esta preventiva é um <b>modelo</b>. A primeira data de execução será calculada
              quando você <b>vincular</b> a preventiva a um equipamento.
            </div>

            <div style={{ marginTop: 12 }}>
              <h4>Itens da Preventiva</h4>

              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {itens.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      border: "1px solid #334155",
                      padding: 12,
                      borderRadius: 8,
                      background: "#0f172a55",
                    }}
                  >
                    <label>
                      <span>Pergunta</span>
                      <input
                        type="text"
                        value={item.pergunta || ""}
                        onChange={(e) =>
                          atualizarItem(index, "pergunta", e.target.value)
                        }
                        style={{
                          width: "100%",
                          marginTop: 4,
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: "1px solid #334155",
                        }}
                      />
                    </label>

                    <label style={{ marginTop: 6 }}>
                      <span>Tipo de Resposta</span>
                      <select
                        value={item.tipoResposta || "SIM_NAO"}
                        onChange={(e) =>
                          atualizarItem(index, "tipoResposta", e.target.value)
                        }
                        style={{
                          width: "100%",
                          marginTop: 4,
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: "1px solid #334155",
                          color: "inherit",
                        }}
                      >
                        <option value="SIM_NAO">Sim / Não</option>
                        <option value="OK_NOK">OK / NOK</option>
                        <option value="BAR">Barra / Numérico</option>
                        <option value="TEXTO">Texto Livre</option>
                      </select>
                    </label>

                    <label style={{ marginTop: 6 }}>
                      <span>Periodicidade do item</span>
                      <select
                        value={item.periodicidade || PERIODICIDADE_ITEM_PADRAO}
                        onChange={(e) =>
                          atualizarItem(index, "periodicidade", e.target.value)
                        }
                        style={{
                          width: "100%",
                          marginTop: 4,
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: "1px solid #334155",
                          color: "inherit",
                        }}
                      >
                        {PERIODICIDADE_ITEM_OPCOES.map((opcao) => (
                          <option key={opcao.value} value={opcao.value}>
                            {opcao.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      <span style={{ fontSize: 13 }}>
                        Foto do item (opcional)
                      </span>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <button
                          type="button"
                          className="pc-btn-secundario"
                          disabled={salvando}
                          onClick={() =>
                            inputItemFotoCameraRefs.current[index]?.click()
                          }
                        >
                          📷 Câmera
                        </button>

                        <button
                          type="button"
                          className="pc-btn-secundario"
                          disabled={salvando}
                          onClick={() =>
                            inputItemFotoArquivoRefs.current[index]?.click()
                          }
                        >
                          ⬆️ Arquivo
                        </button>
                      </div>

                      <input
                        ref={(el) =>
                          (inputItemFotoCameraRefs.current[index] = el)
                        }
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          atualizarFotoItem(index, e.target.files?.[0] || null);
                          if (e.target) e.target.value = "";
                        }}
                      />
                      <input
                        ref={(el) =>
                          (inputItemFotoArquivoRefs.current[index] = el)
                        }
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          atualizarFotoItem(index, e.target.files?.[0] || null);
                          if (e.target) e.target.value = "";
                        }}
                      />

                      {(item.fotoPreview || item.fotoUrl) && (
                        <div
                          style={{
                            marginTop: 6,
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: 8,
                            borderRadius: 10,
                            border: "1px solid #334155",
                          }}
                        >
                          <img
                            src={item.fotoPreview || item.fotoUrl}
                            alt="Foto do item"
                            style={{
                              width: 52,
                              height: 52,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #334155",
                            }}
                          />
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 6,
                            }}
                          >
                            <a
                              href={item.fotoPreview || item.fotoUrl}
                              target="_blank"
                              rel="noreferrer"
                              style={{ fontSize: 12, opacity: 0.9 }}
                            >
                              Abrir
                            </a>
                            <button
                              type="button"
                              className="pc-btn-perigo"
                              style={{ padding: "4px 8px", fontSize: 12 }}
                              onClick={() => removerFotoItem(index)}
                            >
                              Remover
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => removerItem(index)}
                      className="pc-btn-secundario"
                      style={{ marginTop: 8 }}
                    >
                      Remover Item
                    </button>
                  </div>
                ))}

                {itens.length === 0 && (
                  <p style={{ fontSize: 13, opacity: 0.8 }}>
                    Nenhum item configurado ainda. Clique em "Adicionar Item" para
                    começar.
                  </p>
                )}

                <button
                  type="button"
                  className="pc-btn-primario"
                  onClick={adicionarItem}
                  style={{ marginTop: 8 }}
                  disabled={salvando}
                >
                  + Adicionar Item
                </button>
              </div>
            </div>

            <div
              style={{
                marginTop: 18,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/cadastros/preventivas")}
                disabled={salvando}
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="pc-btn-primario"
                disabled={salvando}
              >
                {salvando ? "Salvando..." : "Salvar alterações"}
              </button>
            </div>
          </form>
        )}
      </>
    );
  };

  return isMobile ? (
    <LayoutMobile
      tema={tema}
      alterarTema={alterarTema}
      titulo="Editar Preventiva"
      sidebarAberta={sidebarAberta}
      onToggleSidebar={() => setSidebarAberta((prev) => !prev)}
      abaBottom="cadastros"
    >
      <div className="m-form-container">
        <div className="m-form-header">
          <h2 className="m-form-title">Editar Preventiva</h2>
          <p className="m-form-subtitle">Atualize os dados e salve</p>
        </div>

        <div style={{ marginBottom: 12 }}>
          <button
            type="button"
            className="m-btn-secondary"
            onClick={() => navigate("/cadastros/preventivas")}
          >
            Voltar
          </button>
        </div>

        <div className="m-card">{renderFormulario()}</div>
      </div>
    </LayoutMobile>
  ) : (
    <LayoutDesktop
      tema={tema}
      titulo="Editar Preventiva"
      subtitulo="Cadastros / Preventivas / Editar"
    >
      <section style={{ width: "100%" }}>
        <div
          className="pc-card"
          style={{
            width: "100%",
            paddingLeft: 40,
            paddingRight: 40,
            boxSizing: "border-box",
          }}
        >
          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div>
              <h2 style={{ margin: 0 }}>Editar Preventiva</h2>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                Atualize os dados e salve.
              </p>
            </div>

            <button
              type="button"
              className="pc-btn-secundario"
              onClick={() => navigate("/cadastros/preventivas")}
            >
              Voltar
            </button>
          </header>

          <div style={{ maxWidth: 900, margin: "0 auto" }}>{renderFormulario()}</div>
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default EditarPreventiva;
